import { useState } from 'react';
import {
  Calculator,
  ChevronDown,
  Database,
  Package,
  Receipt,
  Smartphone,
  Tablet,
  Truck,
} from 'lucide-react';
import cn from 'classnames';
import i18n from '@/settings/i18n';

const t = i18n.init('buyback');

export const FAQS: {
  question: string;
  answer: string;
  icon?: React.ReactNode;
}[] = [
  {
    question: 'What devices can I sell on Reebelo?',
    answer: `You can sell smartphones, tablets, laptops, smartwatches and more with Reebelo's trade-in program. Explore all eligible devices at <a href="/buyback" target="_blank" class="text-gray-700 font-medium hover:underline" rel="noopener noreferrer">reebelo.com/buyback</a>.`,
    icon: <Tablet className="h-4 w-4" />,
  },
  {
    question: 'How is my quote calculated?',
    answer: `A preliminary quote is calculated by matching your device's condition against real-time market data to get you the most competitive offer. After our trade-in partner completes inspection, your quote will be confirmed. In the unlikely case that the final quote is different from the preliminary quote, you can choose to accept the counteroffer provided or you can reject the offer and the device will be returned to you.`,

    icon: <Calculator className="h-4 w-4" />,
  },
  {
    question: 'How do I prepare my device before sending it in?',
    answer: `To ensure a fast inspection and a quick payout, follow this simple pre-shipment checklist:
      <ul><li>Remove all passcodes and sign out of your iCloud or Google accounts.</li><li>Take off all cases and screen protectors so our team can see the device clearly.</li><li>Wipe all personal data to give the device a fresh start.</li><li>Send only the device. Keep your SIM cards, cables, and chargers—we don't need them!</li><li>A simple wipe-down to remove dust or fingerprints helps the process move faster.</li></ul>`,

    icon: <Package className="h-4 w-4" />,
  },
  {
    question: 'How do I ship my device?',
    answer: `Once your trade-in is confirmed, we'll email you a pre-paid shipping label. Pack your device in a sturdy box with enough padding (like bubble wrap or paper) to keep it from moving around during transit. Tape your shipping label clearly on the outside of the box, making sure it's easy to scan. Take your package to the designated courier mentioned on your label. Don't forget to ask for a receipt for your tracking records!`,

    icon: <Truck className="h-4 w-4" />,
  },
  {
    question: 'When and how do I get paid?',
    answer: `Once the inspection is complete, you'll receive a secure link to enter your banking details if you selected bank transfer. Once the final offer is confirmed, payment will be issued promptly, typically within 1 business day, to your selected payment method (bank transfer or PayPal).`,

    icon: <Receipt className="h-4 w-4" />,
  },
  {
    question: 'What happens to the data on my device?',
    answer: `Your privacy is our priority. While we recommend performing a factory reset before shipping, our certified refurbishers also conduct a professional data wipe as soon as your device arrives. This ensures your personal information is permanently removed before the device begins its next life.`,

    icon: <Database className="h-4 w-4" />,
  },
  {
    question: "What happens to my device after it's sold?",
    answer: `Once our expert refurbishers professionally wipe your data, they'll repair and polish the device to get it ready for a new owner. If it's seen better days and can't be revived, they'll salvage functional parts and responsibly recycle the rest to keep e-waste out of landfills.`,

    icon: <Smartphone className="h-4 w-4" />,
  },
];

const TradeInFaqs = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="bg-white py-6 xxs:py-8 md:py-10">
      <div className="reebelo-container">
        <h2 className="mb-4 font-bold">{t`Frequently Asked Questions`}</h2>
        <div>
          {FAQS.map((faq, index) => {
            const parsedAnswer = t(faq.answer)
              .replace(/<ul>/g, '<ul class="list-disc">')
              .replace(/<li>/g, '<li class="ml-6">');

            return (
              <div
                key={faq.question}
                className="border-t border-[#DEE2E6] first:border-t-0"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm"
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                >
                  <span
                    className={cn('flex items-center gap-3', {
                      'font-medium': openIndex === index,
                    })}
                  >
                    {faq.icon && (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                        {faq.icon}
                      </span>
                    )}
                    {t(faq.question)}
                  </span>
                  <ChevronDown
                    className={cn('h-4 w-4 shrink-0 transition-transform', {
                      'rotate-180': openIndex === index,
                    })}
                    strokeWidth={1.5}
                  />
                </button>
                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-in-out',
                    openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="ml-8 overflow-hidden">
                    {parsedAnswer.split('\n').map((p) => (
                      <div
                        key={`${faq.question}_${p}`}
                        className="pb-3 text-sm text-gray-700/70 first:mt-0"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: p }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TradeInFaqs;
